import { useState, useEffect } from "react";
import { Check, ExternalLink, RefreshCw, Eye, EyeOff, Palette, LogIn } from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function CanvaIntegrationCard() {
  const [status, setStatus] = useState("loading");
  const [config, setConfig] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    loadConfig();
    checkConnection();
  }, []);

  const loadConfig = async () => {
    try {
      const resp = await authFetch(`${API}/admin/canva/status`);
      if (resp.ok) {
        const data = await resp.json();
        setConfig(data);
        setStatus(data.configured ? "configured" : "not_configured");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Erreur chargement config Canva:", error);
      setStatus("error");
    }
  };

  const checkConnection = async () => {
    try {
      const resp = await authFetch(`${API}/canva/status`);
      if (resp.ok) {
        const data = await resp.json();
        setConnectionStatus(data);
      }
    } catch (error) {
      console.error("Erreur vérification connexion Canva:", error);
    }
  };

  const connectToCanva = async () => {
    setConnecting(true);
    try {
      const resp = await authFetch(`${API}/canva/auth`);
      if (resp.ok) {
        const data = await resp.json();
        // Ouvrir la page d'autorisation Canva
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      console.error("Erreur connexion Canva:", error);
      alert("Erreur lors de la connexion à Canva");
    }
    setConnecting(false);
  };

  const maskSecret = (secret) => {
    if (!secret) return "Non configuré";
    return secret.substring(0, 8) + "•".repeat(20) + secret.substring(secret.length - 4);
  };

  const isConnected = connectionStatus?.connected;

  return (
    <div className={`rounded-xl border p-4 ${
      isConnected 
        ? "bg-green-500/5 border-green-500/30" 
        : status === "configured"
          ? "bg-purple-500/5 border-purple-500/30"
          : "bg-slate-800/50 border-slate-700/50"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isConnected ? "bg-green-500/20" : status === "configured" ? "bg-purple-500/20" : "bg-slate-700/50"
          }`}>
            <Palette className={`w-5 h-5 ${isConnected ? "text-green-400" : status === "configured" ? "text-purple-400" : "text-slate-400"}`} />
          </div>
          <div>
            <h4 className="font-medium flex items-center gap-2">
              Canva API
              {isConnected ? (
                <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                  <Check className="w-3 h-3" /> Connecté
                </span>
              ) : status === "configured" ? (
                <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">Clés OK - Non connecté</span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Non configuré</span>
              )}
            </h4>
            <p className="text-xs text-slate-400">Création de visuels & designs professionnels</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isConnected && status === "configured" && (
            <button
              onClick={connectToCanva}
              disabled={connecting}
              className="px-3 py-1.5 rounded-lg text-xs bg-purple-600 hover:bg-purple-500 transition-colors flex items-center gap-1.5 text-white"
            >
              {connecting ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <LogIn className="w-3 h-3" />
              )}
              Se connecter à Canva
            </button>
          )}
          <button
            onClick={checkConnection}
            className="px-3 py-1.5 rounded-lg text-xs bg-slate-700 hover:bg-slate-600 transition-colors flex items-center gap-1.5"
          >
            <RefreshCw className="w-3 h-3" />
            Actualiser
          </button>
          <a
            href="https://www.canva.com/developers/integrations/connect-api"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 rounded-lg text-xs bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 transition-colors flex items-center gap-1.5"
          >
            <ExternalLink className="w-3 h-3" />
            Console
          </a>
        </div>
      </div>

      {/* Détails de configuration */}
      <div className="mt-4 pt-3 border-t border-slate-700/50 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Client ID:</span>
          <span className="font-mono text-xs text-slate-300">
            {config?.client_id || "Non configuré"}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Client Secret:</span>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-300">
              {showSecret ? config?.client_secret : maskSecret(config?.client_secret)}
            </span>
            <button
              onClick={() => setShowSecret(!showSecret)}
              className="p-1 hover:bg-slate-700 rounded"
            >
              {showSecret ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </button>
          </div>
        </div>
        {isConnected && connectionStatus?.updated_at && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">Connecté depuis:</span>
            <span className="text-xs text-green-400">
              {new Date(connectionStatus.updated_at).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}
      </div>

      {/* Message d'action */}
      {!isConnected && status === "configured" && (
        <div className="mt-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-300 flex items-center gap-2">
            <span className="text-lg">⚠️</span>
            <span>Cliquez sur "Se connecter à Canva" pour autoriser David à créer des designs sur votre compte.</span>
          </p>
        </div>
      )}

      {isConnected && (
        <div className="mt-3 p-2 rounded-lg bg-green-500/10 border border-green-500/20">
          <p className="text-xs text-green-300 flex items-center gap-2">
            <span className="text-lg">✅</span>
            <span>David peut maintenant créer des designs Canva professionnels pour vous !</span>
          </p>
        </div>
      )}
    </div>
  );
}
